// === ANALISI LABEL CLIENTI COMPLETE V4 - 2026-08-12 ===
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import "./AnalisiDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://interventi.synology.me:5050";

const MESI = [
  { value: 1, label: "Gennaio" },
  { value: 2, label: "Febbraio" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Apr" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Aprile" },
  { value: 5, label: "Maggio" },
  { value: 6, label: "Giugno" },
  { value: 7, label: "Luglio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Settembre" },
  { value: 10, label: "Ottobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Dicembre" },
];

const pad2 = (value) => String(value).padStart(2, "0");

const formatNumber = (value, decimals = 0) =>
  Number(value || 0).toLocaleString("it-IT", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

const formatDateIT = (value) => {
  if (!value) return "";

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})/
  );

  if (!match) return String(value);

  return `${match[3]}/${match[2]}/${match[1]}`;
};

const formatPeriodoLabel = (periodo) => {
  if (!periodo) return "";

  if (/^\d{4}-\d{2}$/.test(periodo)) {
    const [year, month] = periodo.split("-").map(Number);

    return new Date(year, month - 1, 1)
      .toLocaleDateString("it-IT", {
        month: "short",
        year: "2-digit",
      })
      .replace(".", "");
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(periodo)) {
    const [year, month, day] = periodo.split("-").map(Number);

    return new Date(year, month - 1, day)
      .toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
      })
      .replace(".", "");
  }

  return periodo;
};

const Comparison = ({ value }) => {
  if (value === null || value === undefined) {
    return (
      <span className="analisi-comparison neutral">
        confronto n/d
      </span>
    );
  }

  const numeric = Number(value);

  if (numeric === 0) {
    return (
      <span className="analisi-comparison neutral">
        = 0%
      </span>
    );
  }

  return (
    <span
      className={`analisi-comparison ${
        numeric > 0 ? "positive" : "negative"
      }`}
    >
      {numeric > 0 ? "↑" : "↓"}{" "}
      {Math.abs(numeric).toLocaleString("it-IT", {
        maximumFractionDigits: 1,
      })}
      %
    </span>
  );
};

const KpiCard = ({
  title,
  value,
  subtitle,
  comparison,
}) => (
  <article className="analisi-kpi-card">
    <div className="analisi-kpi-title">{title}</div>

    <div className="analisi-kpi-value">
      {value}
    </div>

    <div className="analisi-kpi-footer">
      <span>{subtitle}</span>
      <Comparison value={comparison} />
    </div>
  </article>
);

// === ANALISI CLIENTI COMPATTA V6 - 2026-08-12 ===

const splitAnalisiCliente = (value) => {
  const text = String(value || "").trim();
  if (!text) return [""];

  const maxChars =
    typeof window !== "undefined" &&
    window.innerWidth <= 620
      ? 17
      : 24;

  const words = text.split(/\s+/);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current
      ? `${current} ${word}`
      : word;

    if (
      current &&
      candidate.length > maxChars
    ) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });

  if (current) {
    lines.push(current);
  }

  return lines;
};

const AnalisiClienteTick = ({
  x = 0,
  y = 0,
  payload,
}) => {
  const lines =
    splitAnalisiCliente(payload?.value);

  const lineHeight = 13;
  const firstY =
    y -
    ((lines.length - 1) * lineHeight) / 2;

  return (
    <text
      x={x - 7}
      y={firstY}
      textAnchor="end"
      fill="#4d667b"
      fontSize={11}
      fontWeight={500}
    >
      {lines.map((line, index) => (
        <tspan
          key={`${index}-${line}`}
          x={x - 7}
          dy={index === 0 ? 0 : lineHeight}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
};

const AnalisiDashboard = () => {
  const now = new Date();
  const currentYear = now.getFullYear();

  const [tipoPeriodo, setTipoPeriodo] =
    useState("anno");

  const [anno, setAnno] =
    useState(currentYear);

  const [mese, setMese] =
    useState(now.getMonth() + 1);

  const [dalPersonalizzato, setDalPersonalizzato] =
    useState(`${currentYear}-01-01`);

  const [alPersonalizzato, setAlPersonalizzato] =
    useState(
      `${currentYear}-${pad2(now.getMonth() + 1)}-${pad2(
        now.getDate()
      )}`
    );

  const [periodoApplicato, setPeriodoApplicato] =
    useState({
      dal: `${currentYear}-01-01`,
      al: `${currentYear}-${pad2(now.getMonth() + 1)}-${pad2(
  now.getDate()
)}`,
    });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");
  const [ultimoAggiornamento, setUltimoAggiornamento] =
    useState("");

  const anniDisponibili = useMemo(() => {
    const years = [];

    for (let y = currentYear; y >= 2024; y -= 1) {
      years.push(y);
    }

    return years;
  }, [currentYear]);

  const calcolaPeriodo = () => {
    if (tipoPeriodo === "mese") {
      const ultimoGiorno =
        new Date(anno, mese, 0).getDate();

      const oggi =
        `${currentYear}-${pad2(now.getMonth() + 1)}-${pad2(
          now.getDate()
        )}`;

      const meseCorrente =
        anno === currentYear &&
        mese === now.getMonth() + 1;

      return {
        dal: `${anno}-${pad2(mese)}-01`,
        al: meseCorrente
          ? oggi
          : `${anno}-${pad2(mese)}-${pad2(
              ultimoGiorno
            )}`,
      };
    }

    if (tipoPeriodo === "personalizzato") {
      return {
        dal: dalPersonalizzato,
        al: alPersonalizzato,
      };
    }

    const oggi =
      `${currentYear}-${pad2(now.getMonth() + 1)}-${pad2(
        now.getDate()
      )}`;

    return {
      dal: `${anno}-01-01`,
      al:
        anno === currentYear
          ? oggi
          : `${anno}-12-31`,
    };
  };

  const applicaPeriodo = () => {
    const nuovoPeriodo = calcolaPeriodo();

    if (
      !nuovoPeriodo.dal ||
      !nuovoPeriodo.al ||
      nuovoPeriodo.dal > nuovoPeriodo.al
    ) {
      setErrore("Intervallo date non valido.");
      return;
    }

    setErrore("");
    setPeriodoApplicato(nuovoPeriodo);
  };

  useEffect(() => {
    let active = true;

    const fetchStatistiche = async () => {
      setLoading(true);
      setErrore("");

      try {
        const token = localStorage.getItem("token");

        const response = await axios.get(
          `${API_BASE_URL}/statistiche/overview`,
          {
            params: periodoApplicato,
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        if (!active) return;

        setData(response.data);

        setUltimoAggiornamento(
          new Date().toLocaleTimeString("it-IT", {
            hour: "2-digit",
            minute: "2-digit",
          })
        );
      } catch (error) {
        if (!active) return;

        console.error(
          "Errore caricamento Analisi:",
          error
        );

        setErrore(
          error?.response?.data?.message ||
            "Impossibile caricare le statistiche."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchStatistiche();

    return () => {
      active = false;
    };
  }, [periodoApplicato]);

  const kpi = data?.kpi || {};
  const confronto = data?.confronto || {};

  const interventi = kpi.interventi || {};
  const giornate = kpi.giornate || {};
  const preventivi = kpi.preventivi || {};
  const flotta = kpi.flotta || {};
  const materiali = kpi.materiali || {};

  // === ANALISI YOY FRONTEND 2026 ===
  const confrontoAnno =
    data?.confronto_anno || {};

  const annoPrecedente =
    data?.anno_precedente || {};

  const annoCorrenteLabel =
    data?.periodo?.dal?.slice(0, 4) || "";

  const annoPrecedenteLabel =
    data?.periodo_anno_precedente?.dal?.slice(0, 4) || "";

  // === ANALISI TREND MENSILE YOY 2026 ===
  const trendMensileYoY = useMemo(() => {
    const dal = data?.periodo?.dal;
    const al = data?.periodo?.al;

    if (!dal || !al) return [];

    const [annoDal, meseDal] =
      dal.split("-").map(Number);

    const [annoAl, meseAl] =
      al.split("-").map(Number);

    // Il confronto mensile è significativo
    // solo dentro un singolo anno.
    if (annoDal !== annoAl) {
      return [];
    }

    const mesiBrevi = [
      "Gen",
      "Feb",
      "Mar",
      "Apr",
      "Mag",
      "Giu",
      "Lug",
      "Ago",
      "Set",
      "Ott",
      "Nov",
      "Dic",
    ];

    const correnteMap = {};
    const precedenteMap = {};

    /*
      La serie corrente può essere mensile oppure
      giornaliera. Raggruppiamo sempre per mese.
    */
    (data?.serie?.interventi || []).forEach(
      (item) => {
        const parts =
          String(item.periodo || "").split("-");

        const meseNumero =
          Number(parts[1]);

        if (!meseNumero) return;

        correnteMap[meseNumero] =
          Number(
            correnteMap[meseNumero] || 0
          ) +
          Number(item.totale || 0);
      }
    );

    (
      data?.serie_anno_precedente
        ?.interventi || []
    ).forEach((item) => {
      const meseNumero =
        Number(item.mese);

      if (!meseNumero) return;

      precedenteMap[meseNumero] =
        Number(item.totale || 0);
    });

    const result = [];

    for (
      let meseNumero = meseDal;
      meseNumero <= meseAl;
      meseNumero += 1
    ) {
      result.push({
        mese:
          mesiBrevi[meseNumero - 1],

        corrente:
          Number(
            correnteMap[meseNumero] || 0
          ),

        precedente:
          Number(
            precedenteMap[meseNumero] || 0
          ),
      });
    }

    return result;
  }, [
    data?.periodo?.dal,
    data?.periodo?.al,
    data?.serie?.interventi,
    data?.serie_anno_precedente?.interventi,
  ]);

  // === ANALISI PREVENTIVI YOY 2026 ===
  const trendPreventiviYoY = useMemo(() => {
    const dal = data?.periodo?.dal;
    const al = data?.periodo?.al;

    if (!dal || !al) return [];

    const [annoDal, meseDal] =
      dal.split("-").map(Number);

    const [annoAl, meseAl] =
      al.split("-").map(Number);

    if (annoDal !== annoAl) {
      return [];
    }

    const mesiBrevi = [
      "Gen",
      "Feb",
      "Mar",
      "Apr",
      "Mag",
      "Giu",
      "Lug",
      "Ago",
      "Set",
      "Ott",
      "Nov",
      "Dic",
    ];

    const correnteMap = {};
    const precedenteMap = {};

    /*
      La serie corrente può essere giornaliera
      o mensile: sommiamo sempre per mese.
    */
    (data?.serie?.preventivi || []).forEach(
      (item) => {
        const parts =
          String(item.periodo || "").split("-");

        const meseNumero =
          Number(parts[1]);

        if (!meseNumero) return;

        if (!correnteMap[meseNumero]) {
          correnteMap[meseNumero] = {
            valore: 0,
            totale: 0,
          };
        }

        correnteMap[meseNumero].valore +=
          Number(item.valore || 0);

        correnteMap[meseNumero].totale +=
          Number(item.totale || 0);
      }
    );

    (
      data?.serie_anno_precedente
        ?.preventivi || []
    ).forEach((item) => {
      const meseNumero =
        Number(item.mese);

      if (!meseNumero) return;

      precedenteMap[meseNumero] = {
        valore: Number(item.valore || 0),
        totale: Number(item.totale || 0),
      };
    });

    const result = [];

    for (
      let meseNumero = meseDal;
      meseNumero <= meseAl;
      meseNumero += 1
    ) {
      result.push({
        mese:
          mesiBrevi[meseNumero - 1],

        valore_corrente:
          Number(
            correnteMap[meseNumero]?.valore || 0
          ),

        valore_precedente:
          Number(
            precedenteMap[meseNumero]?.valore || 0
          ),

        totale_corrente:
          Number(
            correnteMap[meseNumero]?.totale || 0
          ),

        totale_precedente:
          Number(
            precedenteMap[meseNumero]?.totale || 0
          ),
      });
    }

    return result;
  }, [
    data?.periodo?.dal,
    data?.periodo?.al,
    data?.serie?.preventivi,
    data?.serie_anno_precedente?.preventivi,
  ]);

  const yoyCountData = [
    {
      metrica: "Interventi",
      corrente: Number(interventi.totale || 0),
      precedente: Number(
        annoPrecedente.interventi?.totale || 0
      ),
    },
    {
      metrica: "Clienti",
      corrente: Number(
        interventi.clienti_serviti || 0
      ),
      precedente: Number(
        annoPrecedente.interventi?.clienti_serviti || 0
      ),
    },
    {
      metrica: "Preventivi",
      corrente: Number(preventivi.totale || 0),
      precedente: Number(
        annoPrecedente.preventivi?.totale || 0
      ),
    },
  ];

  const serieInterventi =
    data?.serie?.interventi?.map((item) => ({
      ...item,
      label: formatPeriodoLabel(item.periodo),
    })) || [];

  const seriePreventivi =
    data?.serie?.preventivi?.map((item) => ({
      ...item,
      label: formatPeriodoLabel(item.periodo),
    })) || [];

  // === ANALISI DETTAGLI FRONTEND 2026 ===
  const dettagli = data?.dettagli || {};

  const topClienti =
    dettagli.top_clienti || [];

  const tecnici =
    dettagli.tecnici || [];

  const preventiviStati =
    dettagli.preventivi_stati || [];

// === TOP CLIENTI VALORE PREVENTIVI FRONTEND 2026 ===
const topClientiPreventivi =
  dettagli.top_clienti_preventivi || [];

const quotaTop3Preventivi =
  topClientiPreventivi
    .slice(0, 3)
    .reduce(
      (totale, item) =>
        totale + Number(item.quota_percentuale || 0),
      0
    );


  // === INDICATORI CHIAVE ANALISI 2026 ===
const valoreMedioPreventivo =
  Number(preventivi.totale || 0) > 0
    ? Number(preventivi.valore || 0) /
      Number(preventivi.totale || 1)
    : 0;

const interventiMediCliente =
  Number(interventi.clienti_serviti || 0) > 0
    ? Number(interventi.totale || 0) /
      Number(interventi.clienti_serviti || 1)
    : 0;

const oreMedieIntervento =
  Number(giornate.interventi || 0) > 0
    ? Number(giornate.ore_conteggiate || 0) /
      Number(giornate.interventi || 1)
    : 0;

const percentualeCompletati =
    Number(interventi.totale || 0) > 0
      ? Math.round(
          (Number(interventi.completati || 0) /
            Number(interventi.totale || 1)) *
            100
        )
      : 0;

// === SEGNALAZIONI AUTOMATICHE ANALISI 2026 ===

const percentualeAperti =
  Number(interventi.totale || 0) > 0
    ? (
        Number(interventi.aperti || 0) /
        Number(interventi.totale || 1)
      ) * 100
    : 0;

const coperturaOre =
  Number(interventi.totale || 0) > 0
    ? (
        Number(giornate.interventi || 0) /
        Number(interventi.totale || 1)
      ) * 100
    : 0;

const segnalazioni = [];

const variazionePreventivi =
  confrontoAnno.preventivi_valore;

const variazioneInterventi =
  confrontoAnno.interventi_totale;


// PREVENTIVI
if (
  variazionePreventivi !== null &&
  variazionePreventivi !== undefined
) {
  const valore =
    Number(variazionePreventivi);

  if (valore <= -15) {
    segnalazioni.push({
      tipo: "critical",
      titolo: "Valore preventivi in calo",
      testo:
        `${formatNumber(Math.abs(valore), 1)}% in meno rispetto allo stesso periodo del ${annoPrecedenteLabel}.`,
    });
  } else if (valore < -5) {
    segnalazioni.push({
      tipo: "warning",
      titolo: "Preventivi sotto il periodo precedente",
      testo:
        `Il valore emesso è inferiore del ${formatNumber(Math.abs(valore), 1)}% anno su anno.`,
    });
  } else if (valore >= 20) {
    segnalazioni.push({
      tipo: "success",
      titolo: "Forte crescita dei preventivi",
      testo:
        `Valore preventivi +${formatNumber(valore, 1)}% rispetto allo stesso periodo del ${annoPrecedenteLabel}.`,
    });
  }
}


// INTERVENTI APERTI
if (percentualeAperti >= 35) {
  segnalazioni.push({
    tipo: "critical",
    titolo: "Interventi aperti molto elevati",
    testo:
      `${formatNumber(interventi.aperti)} interventi non completati, pari al ${formatNumber(percentualeAperti, 1)}% del periodo.`,
  });
} else if (percentualeAperti >= 20) {
  segnalazioni.push({
    tipo: "warning",
    titolo: "Interventi aperti da monitorare",
    testo:
      `${formatNumber(interventi.aperti)} interventi ancora non completati (${formatNumber(percentualeAperti, 1)}%).`,
  });
}


// CONCENTRAZIONE CLIENTI
if (quotaTop3Preventivi >= 80) {
  segnalazioni.push({
    tipo: "critical",
    titolo: "Elevata concentrazione commerciale",
    testo:
      `I primi 3 clienti rappresentano il ${formatNumber(quotaTop3Preventivi, 1)}% del valore dei preventivi.`,
  });
} else if (quotaTop3Preventivi >= 60) {
  segnalazioni.push({
    tipo: "warning",
    titolo: "Concentrazione Top 3 significativa",
    testo:
      `Il ${formatNumber(quotaTop3Preventivi, 1)}% del valore preventivi è concentrato sui primi 3 clienti.`,
  });
}


// ORE TECNICI
if (
  Number(interventi.totale || 0) > 0 &&
  coperturaOre < 25
) {
  segnalazioni.push({
    tipo: "info",
    titolo: "Storico ore ancora parziale",
    testo:
      `Le giornate tecnici coprono ${formatNumber(giornate.interventi)} interventi su ${formatNumber(interventi.totale)} (${formatNumber(coperturaOre, 1)}%).`,
  });
} else if (
  Number(giornate.interventi || 0) >= 3 &&
  oreMedieIntervento > 12
) {
  segnalazioni.push({
    tipo: "warning",
    titolo: "Ore medie per intervento elevate",
    testo:
      `La media registrata è di ${formatNumber(oreMedieIntervento, 1)} ore per intervento.`,
  });
}


// VOLUME INTERVENTI
if (
  variazioneInterventi !== null &&
  variazioneInterventi !== undefined
) {
  const valore =
    Number(variazioneInterventi);

  if (valore <= -15) {
    segnalazioni.push({
      tipo: "warning",
      titolo: "Volume interventi in diminuzione",
      testo:
        `${formatNumber(Math.abs(valore), 1)}% di interventi in meno rispetto allo stesso periodo del ${annoPrecedenteLabel}.`,
    });
  } else if (valore >= 25) {
    segnalazioni.push({
      tipo: "success",
      titolo: "Volume interventi in crescita",
      testo:
        `Gli interventi sono aumentati del ${formatNumber(valore, 1)}% anno su anno.`,
    });
  }
}


const prioritaSegnalazione = {
  critical: 1,
  warning: 2,
  info: 3,
  success: 4,
};

const segnalazioniOrdinate =
  [...segnalazioni]
    .sort(
      (a, b) =>
        prioritaSegnalazione[a.tipo] -
        prioritaSegnalazione[b.tipo]
    )
    .slice(0, 6);

const numeroCriticita =
  segnalazioniOrdinate.filter(
    (item) => item.tipo === "critical"
  ).length;

const numeroAttenzioni =
  segnalazioniOrdinate.filter(
    (item) => item.tipo === "warning"
  ).length;

// === EXPORT CSV ANALISI 2026 ===
const exportCSV = () => {
  if (!data) return;

  const esc = (value) =>
    `"${String(value ?? "").replace(/"/g, '""')}"`;

  const rows = [];
  const row = (...values) =>
    rows.push(values.map(esc).join(";"));

  row("ANALISI BETALUMINA");
  row(
    "Periodo",
    data?.periodo?.dal || "",
    data?.periodo?.al || ""
  );

  row();
  row("INDICATORE", "VALORE");

  row("Interventi totali", interventi.totale || 0);
  row("Interventi completati", interventi.completati || 0);
  row("Interventi aperti", interventi.aperti || 0);
  row("Clienti serviti", interventi.clienti_serviti || 0);
  row("Ore conteggiate", giornate.ore_conteggiate || 0);
  row("Preventivi emessi", preventivi.totale || 0);
  row("Valore preventivi", preventivi.valore || 0);
  row("Valore medio preventivo", valoreMedioPreventivo);
  row("Interventi per cliente", interventiMediCliente);
  row("Tasso completamento %", percentualeCompletati);
  row("Ore medie intervento", oreMedieIntervento);
  row("Concentrazione Top 3 %", quotaTop3Preventivi);
  row("Viaggi flotta", flotta.viaggi || 0);
  row("Km flotta", flotta.km || 0);
  row("Materiali utilizzati", materiali.quantita || 0);

  row();
  row(
    "CONFRONTO ANNO SU ANNO",
    annoCorrenteLabel,
    annoPrecedenteLabel,
    "VARIAZIONE %"
  );

  row(
    "Interventi",
    interventi.totale || 0,
    annoPrecedente?.interventi?.totale || 0,
    confrontoAnno.interventi_totale ?? "n/d"
  );

  row(
    "Clienti serviti",
    interventi.clienti_serviti || 0,
    annoPrecedente?.interventi?.clienti_serviti || 0,
    confrontoAnno.clienti_serviti ?? "n/d"
  );

  row(
    "Preventivi",
    preventivi.totale || 0,
    annoPrecedente?.preventivi?.totale || 0,
    confrontoAnno.preventivi_totale ?? "n/d"
  );

  row(
    "Valore preventivi",
    preventivi.valore || 0,
    annoPrecedente?.preventivi?.valore || 0,
    confrontoAnno.preventivi_valore ?? "n/d"
  );

  row();
  row("SEGNALAZIONI", "TIPO", "DETTAGLIO");

  segnalazioniOrdinate.forEach((item) => {
    row(item.titolo, item.tipo, item.testo);
  });

  row();
  row(
    "TOP CLIENTI PER VALORE PREVENTIVI",
    "PREVENTIVI",
    "VALORE",
    "QUOTA %"
  );

  topClientiPreventivi.forEach((item) => {
    row(
      item.cliente,
      item.preventivi || 0,
      item.valore_totale || 0,
      item.quota_percentuale || 0
    );
  });

  const blob = new Blob(
    ["\uFEFF" + rows.join("\r\n")],
    { type: "text/csv;charset=utf-8;" }
  );

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download =
    `analisi_betalumina_${data?.periodo?.dal || "dal"}_${data?.periodo?.al || "al"}.csv`;

  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
};

// === EXPORT PDF ANALISI 2026 ===
// === EXPORT PDF PREMIUM ANALISI 2026 ===
const exportPDF = () => {
  if (!data) return;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const dal = data?.periodo?.dal || "";
  const al = data?.periodo?.al || "";

  const navy = [23, 63, 99];
  const navySoft = [43, 82, 115];
  const slate = [99, 120, 138];
  const light = [244, 247, 249];
  const border = [220, 228, 235];
  const dark = [38, 63, 82];
  const white = [255, 255, 255];

  const euroPDF = (value) =>
    `${Number(value || 0).toLocaleString("it-IT", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} EUR`;

  const shortNumber = (value) => {
    const n = Number(value || 0);

    if (Math.abs(n) >= 1000000) {
      return `${(n / 1000000).toFixed(1)}M`;
    }

    if (Math.abs(n) >= 1000) {
      return `${(n / 1000).toFixed(1)}k`;
    }

    return formatNumber(n);
  };

  const compressSeries = (
    items,
    valueKey,
    maxPoints = 10
  ) => {
    const clean = (items || []).map((item) => ({
      label: item.label || "",
      value: Number(item[valueKey] || 0),
    }));

    if (clean.length <= maxPoints) {
      return clean;
    }

    const groupSize =
      Math.ceil(clean.length / maxPoints);

    const result = [];

    for (
      let i = 0;
      i < clean.length;
      i += groupSize
    ) {
      const chunk =
        clean.slice(i, i + groupSize);

      const value =
        chunk.reduce(
          (sum, item) =>
            sum + Number(item.value || 0),
          0
        );

      const first =
        chunk[0]?.label || "";

      const last =
        chunk[chunk.length - 1]?.label || "";

      result.push({
        label:
          first === last
            ? first
            : `${first}-${last}`,
        value,
      });
    }

    return result;
  };

  const drawKpi = (
    x,
    y,
    width,
    title,
    value,
    subtitle = ""
  ) => {
    doc.setFillColor(...white);
    doc.setDrawColor(...border);
    doc.roundedRect(
      x,
      y,
      width,
      22,
      2.5,
      2.5,
      "FD"
    );

    doc.setTextColor(...slate);
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "bold");
    doc.text(
      String(title).toUpperCase(),
      x + 4,
      y + 6
    );

    doc.setTextColor(...dark);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text(
      String(value),
      x + 4,
      y + 13
    );

    if (subtitle) {
      doc.setTextColor(...slate);
      doc.setFontSize(6.2);
      doc.setFont("helvetica", "normal");

      doc.text(
        String(subtitle),
        x + 4,
        y + 18
      );
    }
  };

  const drawBarChart = (
    title,
    sourceData,
    valueKey,
    y,
    valueFormatter
  ) => {
    const items =
      compressSeries(
        sourceData,
        valueKey,
        10
      );

    const x = 14;
    const width = 182;
    const height = 48;

    doc.setFillColor(...white);
    doc.setDrawColor(...border);

    doc.roundedRect(
      x,
      y,
      width,
      height,
      2.5,
      2.5,
      "FD"
    );

    doc.setTextColor(...dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    doc.text(
      title,
      x + 5,
      y + 7
    );

    if (!items.length) {
      doc.setTextColor(...slate);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);

      doc.text(
        "Nessun dato disponibile nel periodo.",
        x + 5,
        y + 20
      );

      return;
    }

    const chartX = x + 7;
    const chartY = y + 13;
    const chartW = width - 14;
    const chartH = 25;

    const maxValue =
      Math.max(
        ...items.map(
          (item) => Number(item.value || 0)
        ),
        1
      );

    doc.setDrawColor(226, 232, 237);

    for (let grid = 0; grid <= 2; grid++) {
      const gridY =
        chartY +
        (chartH / 2) * grid;

      doc.line(
        chartX,
        gridY,
        chartX + chartW,
        gridY
      );
    }

    const slot =
      chartW / items.length;

    const barWidth =
      Math.max(
        2,
        Math.min(11, slot * 0.56)
      );

    items.forEach((item, index) => {
      const barHeight =
        Math.max(
          item.value > 0 ? 0.8 : 0,
          (Number(item.value || 0) /
            maxValue) *
            chartH
        );

      const barX =
        chartX +
        slot * index +
        (slot - barWidth) / 2;

      const barY =
        chartY +
        chartH -
        barHeight;

      doc.setFillColor(...navySoft);

      doc.roundedRect(
        barX,
        barY,
        barWidth,
        barHeight,
        0.7,
        0.7,
        "F"
      );

      doc.setTextColor(...slate);
      doc.setFontSize(5.2);
      doc.setFont("helvetica", "normal");

      const label =
        String(item.label || "")
          .replace(/\s+/g, " ")
          .slice(0, 11);

      doc.text(
        label,
        barX + barWidth / 2,
        chartY + chartH + 4,
        {
          align: "center",
        }
      );
    });

    doc.setTextColor(...slate);
    doc.setFontSize(5.8);

    doc.text(
      `Max: ${valueFormatter(maxValue)}`,
      x + width - 5,
      y + 7,
      {
        align: "right",
      }
    );
  };


  // ==========================
  // PAGINA 1 — EXECUTIVE
  // ==========================

  doc.setFillColor(...navy);
  doc.rect(
    0,
    0,
    210,
    31,
    "F"
  );

  doc.setTextColor(...white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);

  doc.text(
    "BETALUMINA",
    14,
    13
  );

  doc.setFontSize(10);
  doc.setFont(
    "helvetica",
    "normal"
  );

  doc.text(
    "Analisi | Controllo di gestione",
    14,
    20
  );

  doc.setFontSize(7);

  doc.text(
    `Periodo ${formatDateIT(dal)} - ${formatDateIT(al)}`,
    14,
    26
  );

  doc.text(
    `Generato ${new Date().toLocaleString("it-IT")}`,
    196,
    26,
    {
      align: "right",
    }
  );

  doc.setFillColor(...light);
  doc.rect(
    0,
    31,
    210,
    266,
    "F"
  );

  doc.setTextColor(...dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);

  doc.text(
    "Sintesi direzionale",
    14,
    42
  );

  const cardWidth = 57.3;

  drawKpi(
    14,
    47,
    cardWidth,
    "Interventi",
    formatNumber(interventi.totale),
    `${formatNumber(interventi.completati)} completati`
  );

  drawKpi(
    76.3,
    47,
    cardWidth,
    "Clienti serviti",
    formatNumber(
      interventi.clienti_serviti
    ),
    `${formatNumber(interventiMediCliente, 1)} interventi / cliente`
  );

  drawKpi(
    138.6,
    47,
    cardWidth,
    "Completamento",
    `${formatNumber(percentualeCompletati, 1)}%`,
    `${formatNumber(interventi.aperti)} aperti`
  );

  drawKpi(
    14,
    73,
    cardWidth,
    "Valore preventivi",
    euroPDF(preventivi.valore),
    `${formatNumber(preventivi.totale)} emessi`
  );

  drawKpi(
    76.3,
    73,
    cardWidth,
    "Valore medio",
    euroPDF(valoreMedioPreventivo),
    "per preventivo"
  );

  drawKpi(
    138.6,
    73,
    cardWidth,
    "Ore conteggiate",
    `${formatNumber(
      giornate.ore_conteggiate,
      1
    )} h`,
    `${formatNumber(oreMedieIntervento, 1)} h / intervento`
  );

  drawBarChart(
    "Andamento interventi",
    serieInterventi,
    "totale",
    104,
    shortNumber
  );

  drawBarChart(
    "Valore preventivi",
    seriePreventivi,
    "valore",
    158,
    (value) =>
      `${shortNumber(value)} EUR`
  );

  doc.setFillColor(
    236,
    242,
    247
  );

  doc.setDrawColor(...border);

  doc.roundedRect(
    14,
    214,
    182,
    28,
    2.5,
    2.5,
    "FD"
  );

  doc.setTextColor(...slate);
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");

  doc.text(
    "CONCENTRAZIONE COMMERCIALE",
    19,
    222
  );

  doc.setTextColor(...dark);
  doc.setFontSize(14);

  doc.text(
    `${formatNumber(
      quotaTop3Preventivi,
      1
    )}%`,
    19,
    232
  );

  doc.setTextColor(...slate);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");

  doc.text(
    "del valore preventivi è concentrato sui primi 3 clienti.",
    42,
    232
  );

  doc.setTextColor(...slate);
  doc.setFontSize(6.5);

  doc.text(
    "I grafici sintetizzano il periodo selezionato; su intervalli molto dettagliati i dati vengono raggruppati solo ai fini visuali.",
    14,
    252,
    {
      maxWidth: 182,
    }
  );


  // ==========================
  // PAGINA 2 — DETTAGLIO
  // ==========================

  doc.addPage();

  doc.setFillColor(...navy);
  doc.rect(
    0,
    0,
    210,
    23,
    "F"
  );

  doc.setTextColor(...white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);

  doc.text(
    "BETALUMINA | Dettaglio Analisi",
    14,
    14
  );

  doc.setTextColor(...dark);
  doc.setFontSize(11);

  doc.text(
    "Confronto anno su anno",
    14,
    33
  );

  autoTable(doc, {
    startY: 38,

    head: [[
      "Indicatore",
      annoCorrenteLabel,
      annoPrecedenteLabel,
      "Var. %"
    ]],

    body: [
      [
        "Interventi",
        formatNumber(
          interventi.totale
        ),
        formatNumber(
          annoPrecedente
            ?.interventi
            ?.totale
        ),
        confrontoAnno
          .interventi_totale != null
          ? `${formatNumber(
              confrontoAnno
                .interventi_totale,
              1
            )}%`
          : "n/d",
      ],

      [
        "Interventi completati",
        formatNumber(
          interventi.completati
        ),
        formatNumber(
          annoPrecedente
            ?.interventi
            ?.completati
        ),
        confrontoAnno
          .interventi_completati != null
          ? `${formatNumber(
              confrontoAnno
                .interventi_completati,
              1
            )}%`
          : "n/d",
      ],

      [
        "Clienti serviti",
        formatNumber(
          interventi.clienti_serviti
        ),
        formatNumber(
          annoPrecedente
            ?.interventi
            ?.clienti_serviti
        ),
        confrontoAnno
          .clienti_serviti != null
          ? `${formatNumber(
              confrontoAnno
                .clienti_serviti,
              1
            )}%`
          : "n/d",
      ],

      [
        "Preventivi",
        formatNumber(
          preventivi.totale
        ),
        formatNumber(
          annoPrecedente
            ?.preventivi
            ?.totale
        ),
        confrontoAnno
          .preventivi_totale != null
          ? `${formatNumber(
              confrontoAnno
                .preventivi_totale,
              1
            )}%`
          : "n/d",
      ],

      [
        "Valore preventivi",
        euroPDF(
          preventivi.valore
        ),
        euroPDF(
          annoPrecedente
            ?.preventivi
            ?.valore
        ),
        confrontoAnno
          .preventivi_valore != null
          ? `${formatNumber(
              confrontoAnno
                .preventivi_valore,
              1
            )}%`
          : "n/d",
      ],
    ],

    theme: "grid",

    styles: {
      fontSize: 8,
      cellPadding: 2.8,
      textColor: dark,
      lineColor: border,
      lineWidth: 0.2,
    },

    headStyles: {
      fillColor: navy,
      textColor: white,
      fontStyle: "bold",
    },

    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });


  const alertRows =
    segnalazioniOrdinate.length
      ? segnalazioniOrdinate.map(
          (item) => [
            item.tipo === "critical"
              ? "CRITICITA"
              : item.tipo === "warning"
              ? "ATTENZIONE"
              : item.tipo === "success"
              ? "POSITIVO"
              : "INFO",
            item.titolo,
            item.testo,
          ]
        )
      : [[
          "OK",
          "Nessuna segnalazione",
          "Nessuna segnalazione rilevante nel periodo selezionato.",
        ]];

  let nextY =
    doc.lastAutoTable.finalY + 11;

  doc.setTextColor(...dark);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");

  doc.text(
    "Segnalazioni",
    14,
    nextY
  );

  autoTable(doc, {
    startY: nextY + 5,

    head: [[
      "Tipo",
      "Segnalazione",
      "Dettaglio"
    ]],

    body: alertRows,

    theme: "grid",

    styles: {
      fontSize: 7.4,
      cellPadding: 2.5,
      textColor: dark,
      lineColor: border,
      lineWidth: 0.2,
      overflow: "linebreak",
    },

    headStyles: {
      fillColor: navy,
      textColor: white,
    },

    columnStyles: {
      0: {
        cellWidth: 23,
      },
      1: {
        cellWidth: 50,
      },
    },
  });


  if (topClientiPreventivi.length) {
    nextY =
      doc.lastAutoTable.finalY + 11;

    if (nextY > 245) {
      doc.addPage();

      doc.setFillColor(...navy);
      doc.rect(
        0,
        0,
        210,
        23,
        "F"
      );

      doc.setTextColor(...white);
      doc.setFont(
        "helvetica",
        "bold"
      );
      doc.setFontSize(13);

      doc.text(
        "BETALUMINA | Analisi commerciale",
        14,
        14
      );

      nextY = 33;
    }

    doc.setTextColor(...dark);
    doc.setFontSize(11);
    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.text(
      "Top clienti per valore preventivi",
      14,
      nextY
    );

    autoTable(doc, {
      startY: nextY + 5,

      head: [[
        "Cliente",
        "Preventivi",
        "Valore",
        "Media",
        "Quota"
      ]],

      body:
        topClientiPreventivi.map(
          (item) => [
            item.cliente || "-",
            formatNumber(
              item.preventivi
            ),
            euroPDF(
              item.valore_totale
            ),
            euroPDF(
              item.valore_medio
            ),
            `${formatNumber(
              item.quota_percentuale,
              1
            )}%`,
          ]
        ),

      theme: "grid",

      styles: {
        fontSize: 7.4,
        cellPadding: 2.4,
        textColor: dark,
        lineColor: border,
        lineWidth: 0.2,
      },

      headStyles: {
        fillColor: navy,
        textColor: white,
      },

      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },

      columnStyles: {
        0: {
          cellWidth: 62,
        },
      },
    });
  }


  // ==========================
  // FOOTER PAGINE
  // ==========================

  const pageCount =
    doc.getNumberOfPages();

  for (
    let page = 1;
    page <= pageCount;
    page++
  ) {
    doc.setPage(page);

    doc.setDrawColor(...border);

    doc.line(
      14,
      286,
      196,
      286
    );

    doc.setTextColor(...slate);
    doc.setFontSize(6.5);
    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.text(
      "Betalumina | Analisi gestionale",
      14,
      291
    );

    doc.text(
      `Pagina ${page} di ${pageCount}`,
      196,
      291,
      {
        align: "right",
      }
    );
  }

  doc.save(
    `analisi_betalumina_${dal || "dal"}_${al || "al"}.pdf`
  );
};




  return (
    <section className="analisi-dashboard">
      <header className="analisi-header">
        <div>
          <span className="analisi-eyebrow">
            Controllo di gestione
          </span>

          <h2>Analisi</h2>

          <p>
            Indicatori globali di interventi,
            preventivi, tecnici, materiali e flotta.
          </p>
        </div>

        {ultimoAggiornamento && (
          <div className="analisi-last-update">
            Aggiornato alle {ultimoAggiornamento}
          </div>
        )}
      </header>

      <div className="analisi-export-bar">
        <span>Report periodo selezionato</span>

        <button
          type="button"
          className="analisi-export-btn"
          onClick={exportCSV}
          disabled={!data}
        >
          Esporta CSV
        </button>

        <button
          type="button"
          className="analisi-export-btn"
          onClick={exportPDF}
          disabled={!data}
        >
          Esporta PDF
        </button>
      </div>

      <div className="analisi-filtri">
        <div className="analisi-filter-field">
          <label>Periodo</label>

          <select
            value={tipoPeriodo}
            onChange={(e) =>
              setTipoPeriodo(e.target.value)
            }
          >
            <option value="anno">
              Annuale
            </option>
            <option value="mese">
              Mensile
            </option>
            <option value="personalizzato">
              Personalizzato
            </option>
          </select>
        </div>

        {tipoPeriodo !== "personalizzato" && (
          <div className="analisi-filter-field">
            <label>Anno</label>

            <select
              value={anno}
              onChange={(e) =>
                setAnno(Number(e.target.value))
              }
            >
              {anniDisponibili.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        )}

        {tipoPeriodo === "mese" && (
          <div className="analisi-filter-field">
            <label>Mese</label>

            <select
              value={mese}
              onChange={(e) =>
                setMese(Number(e.target.value))
              }
            >
              {MESI.map((item) => (
                <option
                  key={item.value}
                  value={item.value}
                >
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {tipoPeriodo === "personalizzato" && (
          <>
            <div className="analisi-filter-field">
              <label>Dal</label>

              <input
                type="date"
                value={dalPersonalizzato}
                onChange={(e) =>
                  setDalPersonalizzato(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="analisi-filter-field">
              <label>Al</label>

              <input
                type="date"
                value={alPersonalizzato}
                onChange={(e) =>
                  setAlPersonalizzato(
                    e.target.value
                  )
                }
              />
            </div>
          </>
        )}

        <button
          type="button"
          className="analisi-apply-button"
          onClick={applicaPeriodo}
        >
          Applica periodo
        </button>
      </div>

      {data?.periodo && (
        <div className="analisi-period-banner">
          <strong>
            {formatDateIT(data.periodo.dal)}
          </strong>
          <span>→</span>
          <strong>
            {formatDateIT(data.periodo.al)}
          </strong>

          <span className="analisi-period-days">
            {data.periodo.giorni} giorni
          </span>

          <span className="analisi-period-compare">
            confronto con{" "}
            {formatDateIT(data.periodo_anno_precedente?.dal)} →{" "}
            {formatDateIT(data.periodo_anno_precedente?.al)}
          </span>
        </div>
      )}

      {errore && (
        <div className="analisi-error">
          {errore}
        </div>
      )}

      {loading ? (
        <div className="analisi-loading">
          <div className="analisi-spinner" />
          <span>
            Calcolo statistiche in corso...
          </span>
        </div>
      ) : (
        <>
          <div className="analisi-kpi-grid">
            <KpiCard
              title="Interventi"
              value={formatNumber(
                interventi.totale
              )}
              subtitle={`${formatNumber(
                interventi.completati
              )} completati`}
              comparison={
                confronto.interventi_totale
              }
            />

            <KpiCard
              title="Clienti serviti"
              value={formatNumber(
                interventi.clienti_serviti
              )}
              subtitle="nel periodo"
              comparison={
                confronto.clienti_serviti
              }
            />

            <KpiCard
              title="Ore conteggiate"
              value={`${formatNumber(
                giornate.ore_conteggiate,
                1
              )} h`}
              subtitle={`${formatNumber(
                giornate.ore_effettive,
                1
              )} h effettive`}
              comparison={
                confronto.ore_conteggiate
              }
            />

            <KpiCard
              title="Preventivi"
              value={formatNumber(
                preventivi.totale
              )}
              subtitle="emessi"
              comparison={
                confronto.preventivi_totale
              }
            />

            <KpiCard
              title="Valore preventivi"
              value={formatCurrency(
                preventivi.valore
              )}
              subtitle="valore complessivo"
              comparison={
                confronto.preventivi_valore
              }
            />

            <KpiCard
              title="Viaggi flotta"
              value={formatNumber(
                flotta.viaggi
              )}
              subtitle={`${formatNumber(
                flotta.km,
                1
              )} km registrati`}
              comparison={
                confronto.flotta_viaggi
              }
            />

            <KpiCard
              title="Materiali usati"
              value={formatNumber(
                materiali.quantita,
                1
              )}
              subtitle={formatCurrency(
                materiali.valore
              )}
              comparison={
                confronto.materiali_quantita
              }
            />

            <article className="analisi-kpi-card analisi-completion-card">
              <div className="analisi-kpi-title">
                Completamento
              </div>

              <div className="analisi-kpi-value">
                {percentualeCompletati}%
              </div>

              <div className="analisi-progress">
                <span
                  style={{
                    width: `${Math.min(
                      percentualeCompletati,
                      100
                    )}%`,
                  }}
                />
              </div>

              <div className="analisi-kpi-footer">
                <span>
                  {formatNumber(
                    interventi.aperti
                  )}{" "}
                  non completati
                </span>

                <Comparison
                  value={
                    confronto.interventi_completati
                  }
                />
              </div>
            </article>
          </div>

          <section className="analisi-indicatori-section">
        <div className="analisi-indicatori-header">
          <div>
            <span className="analisi-eyebrow">
              Sintesi direzionale
            </span>
            <h3>Indicatori chiave</h3>
          </div>

          <span>Periodo selezionato</span>
        </div>

        <div className="analisi-indicatori-grid">
          <article className="analisi-indicatore">
            <span>Valore medio preventivo</span>
            <strong>
              {formatCurrency(valoreMedioPreventivo)}
            </strong>
            <small>
              valore medio per preventivo emesso
            </small>
          </article>

          <article className="analisi-indicatore">
            <span>Interventi per cliente</span>
            <strong>
              {formatNumber(interventiMediCliente, 1)}
            </strong>
            <small>
              media per cliente servito
            </small>
          </article>

          <article className="analisi-indicatore">
            <span>Tasso completamento</span>
            <strong>
              {percentualeCompletati}%
            </strong>
            <small>
              {formatNumber(interventi.completati)} su{" "}
              {formatNumber(interventi.totale)}
            </small>
          </article>

          <article className="analisi-indicatore">
            <span>Ore medie intervento</span>
            <strong>
              {formatNumber(oreMedieIntervento, 1)} h
            </strong>
            <small>
              sugli interventi con giornate registrate
            </small>
          </article>

          <article className="analisi-indicatore">
            <span>Concentrazione Top 3</span>
            <strong>
              {formatNumber(quotaTop3Preventivi, 1)}%
            </strong>
            <small>
              quota valore preventivi dei primi 3 clienti
            </small>
          </article>
        </div>
      </section>

      
      <section className="analisi-alert-section">
        <div className="analisi-alert-header">
          <div>
            <span className="analisi-eyebrow">
              Monitoraggio automatico
            </span>

            <h3>Segnalazioni</h3>

            <p>
              Indicatori che meritano attenzione
              nel periodo selezionato.
            </p>
          </div>

          <div className="analisi-alert-summary">
            {numeroCriticita > 0 && (
              <span className="critical">
                {numeroCriticita} criticità
              </span>
            )}

            {numeroAttenzioni > 0 && (
              <span className="warning">
                {numeroAttenzioni} attenzioni
              </span>
            )}

            {numeroCriticita === 0 &&
              numeroAttenzioni === 0 && (
                <span className="ok">
                  Nessuna criticità
                </span>
              )}
          </div>
        </div>

        {segnalazioniOrdinate.length > 0 ? (
          <div className="analisi-alert-grid">
            {segnalazioniOrdinate.map(
              (item, index) => (
                <article
                  key={`${item.tipo}-${index}`}
                  className={`analisi-alert analisi-alert-${item.tipo}`}
                >
                  <span className="analisi-alert-icon">
                    {item.tipo === "critical"
                      ? "!"
                      : item.tipo === "warning"
                      ? "!"
                      : item.tipo === "success"
                      ? "✓"
                      : "i"}
                  </span>

                  <div>
                    <strong>
                      {item.titolo}
                    </strong>

                    <p>
                      {item.testo}
                    </p>
                  </div>
                </article>
              )
            )}
          </div>
        ) : (
          <div className="analisi-alert-empty">
            Nessuna segnalazione rilevante
            per il periodo selezionato.
          </div>
        )}
      </section>

      <div className="analisi-chart-grid">
            <article className="analisi-chart-card">
              <div className="analisi-chart-heading">
                <div>
                  <span>OPERATIVITÀ</span>
                  <h3>
                    Andamento interventi
                  </h3>
                </div>

                <strong>
                  {formatNumber(
                    interventi.totale
                  )}
                </strong>
              </div>

              {serieInterventi.length > 0 ? (
                <div className="analisi-chart">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <LineChart
                      data={serieInterventi}
                      margin={{
                        top: 12,
                        right: 14,
                        left: -14,
                        bottom: 4,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#dce5ee"
                      />

                      <XAxis
                        dataKey="label"
                        tick={{
                          fill: "#62778a",
                          fontSize: 12,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        allowDecimals={false}
                        tick={{
                          fill: "#62778a",
                          fontSize: 12,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <Tooltip
                        formatter={(value) => [
                          formatNumber(value),
                          "Interventi",
                        ]}
                        contentStyle={{
                          borderRadius: 12,
                          border:
                            "1px solid #d9e3ec",
                        }}
                      />

                      <Line
                        type="monotone"
                        dataKey="totale"
                        stroke="#2f6799"
                        strokeWidth={3}
                        dot={{
                          r: 3,
                          fill: "#2f6799",
                        }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="analisi-empty">
                  Nessun intervento nel periodo.
                </div>
              )}
            </article>

            <article className="analisi-chart-card">
              <div className="analisi-chart-heading">
                <div>
                  <span>COMMERCIALE</span>
                  <h3>
                    Valore preventivi
                  </h3>
                </div>

                <strong>
                  {formatCurrency(
                    preventivi.valore
                  )}
                </strong>
              </div>

              {seriePreventivi.length > 0 ? (
                <div className="analisi-chart">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={seriePreventivi}
                      margin={{
                        top: 12,
                        right: 14,
                        left: 0,
                        bottom: 4,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#dce5ee"
                      />

                      <XAxis
                        dataKey="label"
                        tick={{
                          fill: "#62778a",
                          fontSize: 12,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        tickFormatter={(value) =>
                          value >= 1000
                            ? `${Math.round(
                                value / 1000
                              )}k`
                            : value
                        }
                        tick={{
                          fill: "#62778a",
                          fontSize: 12,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <Tooltip
                        formatter={(value) => [
                          formatCurrency(value),
                          "Valore",
                        ]}
                        contentStyle={{
                          borderRadius: 12,
                          border:
                            "1px solid #d9e3ec",
                        }}
                      />

                      <Bar
                        dataKey="valore"
                        fill="#315f8f"
                        radius={[7, 7, 2, 2]}
                        maxBarSize={42}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="analisi-empty">
                  Nessun preventivo nel periodo.
                </div>
              )}
            </article>
          </div>

          <section className="analisi-yoy-section">
            <div className="analisi-yoy-header">
              <div>
                <span className="analisi-eyebrow">
                  Confronto storico
                </span>

                <h3>
                  {annoCorrenteLabel} vs{" "}
                  {annoPrecedenteLabel}
                </h3>

                <p>
                  Stesso intervallo temporale:
                  {" "}
                  {formatDateIT(data?.periodo?.dal)} →{" "}
                  {formatDateIT(data?.periodo?.al)}
                  {" "}contro{" "}
                  {formatDateIT(data?.periodo_anno_precedente?.dal)}
                  {" "}→{" "}
                  {formatDateIT(data?.periodo_anno_precedente?.al)}
                </p>
              </div>

              <span className="analisi-yoy-badge">
                Anno su anno
              </span>
            </div>

            <div className="analisi-yoy-layout">
              <article className="analisi-chart-card">
                <div className="analisi-chart-heading">
                  <div>
                    <span>VOLUMI</span>
                    <h3>
                      Attività a confronto
                    </h3>
                  </div>
                </div>

                <div className="analisi-chart analisi-yoy-chart">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={yoyCountData}
                      margin={{
                        top: 10,
                        right: 12,
                        left: -10,
                        bottom: 4,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#dce5ee"
                      />

                      <XAxis
                        dataKey="metrica"
                        tick={{
                          fill: "#526a7f",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        allowDecimals={false}
                        tick={{
                          fill: "#718596",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <Tooltip
                        formatter={(value, name) => [
                          formatNumber(value),
                          name === "corrente"
                            ? annoCorrenteLabel
                            : annoPrecedenteLabel,
                        ]}
                        contentStyle={{
                          borderRadius: 12,
                          border:
                            "1px solid #d9e3ec",
                        }}
                      />

                      <Bar
                        dataKey="precedente"
                        fill="#a9b9c7"
                        radius={[6, 6, 2, 2]}
                        maxBarSize={45}
                      />

                      <Bar
                        dataKey="corrente"
                        fill="#315f8f"
                        radius={[6, 6, 2, 2]}
                        maxBarSize={45}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="analisi-yoy-legend">
                  <span>
                    <i className="previous" />
                    {annoPrecedenteLabel}
                  </span>

                  <span>
                    <i className="current" />
                    {annoCorrenteLabel}
                  </span>
                </div>
              </article>

              <div className="analisi-yoy-metrics">
                <article className="analisi-yoy-card">
                  <span>Interventi</span>

                  <strong>
                    {formatNumber(
                      interventi.totale
                    )}
                  </strong>

                  <small>
                    {formatNumber(
                      annoPrecedente
                        .interventi?.totale
                    )}{" "}
                    nel {annoPrecedenteLabel}
                  </small>

                  <Comparison
                    value={
                      confrontoAnno
                        .interventi_totale
                    }
                  />
                </article>

                <article className="analisi-yoy-card">
                  <span>Clienti serviti</span>

                  <strong>
                    {formatNumber(
                      interventi.clienti_serviti
                    )}
                  </strong>

                  <small>
                    {formatNumber(
                      annoPrecedente
                        .interventi
                        ?.clienti_serviti
                    )}{" "}
                    nel {annoPrecedenteLabel}
                  </small>

                  <Comparison
                    value={
                      confrontoAnno
                        .clienti_serviti
                    }
                  />
                </article>

                <article className="analisi-yoy-card">
                  <span>
                    Valore preventivi
                  </span>

                  <strong>
                    {formatCurrency(
                      preventivi.valore
                    )}
                  </strong>

                  <small>
                    {formatCurrency(
                      annoPrecedente
                        .preventivi?.valore
                    )}{" "}
                    nel {annoPrecedenteLabel}
                  </small>

                  <Comparison
                    value={
                      confrontoAnno
                        .preventivi_valore
                    }
                  />
                </article>

                <article className="analisi-yoy-card">
                  <span>
                    Interventi completati
                  </span>

                  <strong>
                    {formatNumber(
                      interventi.completati
                    )}
                  </strong>

                  <small>
                    {formatNumber(
                      annoPrecedente
                        .interventi
                        ?.completati
                    )}{" "}
                    nel {annoPrecedenteLabel}
                  </small>

                  <Comparison
                    value={
                      confrontoAnno
                        .interventi_completati
                    }
                  />
                </article>
              </div>
            </div>

            {trendMensileYoY.length > 0 && (
              <article className="analisi-chart-card analisi-yoy-trend-card">
                <div className="analisi-chart-heading">
                  <div>
                    <span>TREND MENSILE</span>

                    <h3>
                      Interventi mese per mese
                    </h3>
                  </div>

                  <div className="analisi-yoy-trend-years">
                    <span className="previous">
                      {annoPrecedenteLabel}
                    </span>

                    <span className="current">
                      {annoCorrenteLabel}
                    </span>
                  </div>
                </div>

                <div className="analisi-chart analisi-yoy-trend-chart">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <LineChart
                      data={trendMensileYoY}
                      margin={{
                        top: 14,
                        right: 18,
                        left: -12,
                        bottom: 4,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#dce5ee"
                      />

                      <XAxis
                        dataKey="mese"
                        tick={{
                          fill: "#5f7487",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        allowDecimals={false}
                        tick={{
                          fill: "#718596",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <Tooltip
                        formatter={(value, name) => [
                          formatNumber(value),
                          name === "corrente"
                            ? annoCorrenteLabel
                            : annoPrecedenteLabel,
                        ]}
                        contentStyle={{
                          borderRadius: 12,
                          border:
                            "1px solid #d9e3ec",
                        }}
                      />

                      <Line
                        type="monotone"
                        dataKey="precedente"
                        name="precedente"
                        stroke="#9aabba"
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={{
                          r: 3,
                          fill: "#9aabba",
                        }}
                        activeDot={{ r: 5 }}
                      />

                      <Line
                        type="monotone"
                        dataKey="corrente"
                        name="corrente"
                        stroke="#2f6799"
                        strokeWidth={3}
                        dot={{
                          r: 4,
                          fill: "#2f6799",
                        }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </article>
            )}

            {trendPreventiviYoY.length > 0 && (
              <article className="analisi-chart-card analisi-yoy-trend-card">
                <div className="analisi-chart-heading">
                  <div>
                    <span>PREVENTIVI · TREND MENSILE</span>

                    <h3>
                      Valore preventivi mese per mese
                    </h3>
                  </div>

                  <div className="analisi-yoy-trend-years">
                    <span className="previous">
                      {annoPrecedenteLabel}
                    </span>

                    <span className="current">
                      {annoCorrenteLabel}
                    </span>
                  </div>
                </div>

                <div className="analisi-chart analisi-yoy-trend-chart">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <LineChart
                      data={trendPreventiviYoY}
                      margin={{
                        top: 14,
                        right: 18,
                        left: 4,
                        bottom: 4,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#dce5ee"
                      />

                      <XAxis
                        dataKey="mese"
                        tick={{
                          fill: "#5f7487",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        tickFormatter={(value) =>
                          value >= 1000
                            ? `${Math.round(
                                value / 1000
                              )}k`
                            : value
                        }
                        tick={{
                          fill: "#718596",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <Tooltip
                        formatter={(value, name) => [
                          formatCurrency(value),
                          name === "valore_corrente"
                            ? annoCorrenteLabel
                            : annoPrecedenteLabel,
                        ]}
                        contentStyle={{
                          borderRadius: 12,
                          border:
                            "1px solid #d9e3ec",
                        }}
                      />

                      <Line
                        type="monotone"
                        dataKey="valore_precedente"
                        name="valore_precedente"
                        stroke="#9aabba"
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={{
                          r: 3,
                          fill: "#9aabba",
                        }}
                        activeDot={{ r: 5 }}
                      />

                      <Line
                        type="monotone"
                        dataKey="valore_corrente"
                        name="valore_corrente"
                        stroke="#2f6799"
                        strokeWidth={3}
                        dot={{
                          r: 4,
                          fill: "#2f6799",
                        }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="analisi-yoy-summary">
                  <div>
                    <span>
                      {annoCorrenteLabel}
                    </span>

                    <strong>
                      {formatCurrency(
                        preventivi.valore
                      )}
                    </strong>

                    <small>
                      {formatNumber(
                        preventivi.totale
                      )}{" "}
                      preventivi
                    </small>
                  </div>

                  <div>
                    <span>
                      {annoPrecedenteLabel}
                    </span>

                    <strong>
                      {formatCurrency(
                        annoPrecedente
                          .preventivi?.valore
                      )}
                    </strong>

                    <small>
                      {formatNumber(
                        annoPrecedente
                          .preventivi?.totale
                      )}{" "}
                      preventivi
                    </small>
                  </div>

                  <div>
                    <span>
                      Variazione valore
                    </span>

                    <strong>
                      <Comparison
                        value={
                          confrontoAnno
                            .preventivi_valore
                        }
                      />
                    </strong>
                  </div>
                </div>
              </article>
            )}

            <div className="analisi-yoy-note">
              Ore tecnici e Flotta mostrano
              confronto n/d quando non esiste uno
              storico equivalente nell'anno precedente.
            </div>
          </section>

          
        <article className="analisi-chart-card analisi-clienti-valore-card">
          <div className="analisi-chart-heading">
            <div>
              <span>COMMERCIALE · CLIENTI</span>
              <h3>Top clienti per valore preventivi</h3>
            </div>

            {topClientiPreventivi.length > 0 && (
              <div className="analisi-clienti-concentrazione">
                <span>Quota Top 3</span>
                <strong>
                  {formatNumber(quotaTop3Preventivi, 1)}%
                </strong>
              </div>
            )}
          </div>

          {topClientiPreventivi.length > 0 ? (
            <>
              <div className="analisi-chart analisi-clienti-valore-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topClientiPreventivi}
                    layout="vertical"
                    margin={{
                      top: 8,
                      right: 28,
                      left: 12,
                      bottom: 4,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      stroke="#dce5ee"
                    />

                    <XAxis
                      type="number"
                      tickFormatter={(value) =>
                        value >= 1000
                          ? `${Math.round(value / 1000)}k`
                          : value
                      }
                      tick={{
                        fill: "#657b8e",
                        fontSize: 11,
                      }}
                      tickLine={false}
                      axisLine={false}
                    />

                    <YAxis
                      type="category"
                      dataKey="cliente"
                      width={205}
                      interval={0}
                      tick={<AnalisiClienteTick />}
                      tickLine={false}
                      axisLine={false}
                    />

                    <Tooltip
                      formatter={(value) => [
                        formatCurrency(value),
                        "Valore preventivi",
                      ]}
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #d9e3ec",
                      }}
                    />

                    <Bar
                      dataKey="valore_totale"
                      fill="#315f8f"
                      radius={[0, 7, 7, 0]}
                      maxBarSize={25}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="analisi-clienti-valore-list">
                {topClientiPreventivi.map((item, index) => (
                  <div
                    className="analisi-clienti-valore-row"
                    key={`${item.cliente}-${index}`}
                  >
                    <span className="analisi-clienti-rank">
                      {index + 1}
                    </span>

                    <div className="analisi-clienti-name">
                      <strong>{item.cliente}</strong>
                      <small>
                        {formatNumber(item.preventivi)} preventivi · media{" "}
                        {formatCurrency(item.valore_medio)}
                      </small>
                    </div>

                    <div className="analisi-clienti-total">
                      <strong>
                        {formatCurrency(item.valore_totale)}
                      </strong>
                      <small>
                        {formatNumber(item.quota_percentuale, 1)}% del totale
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="analisi-empty">
              Nessun preventivo cliente nel periodo.
            </div>
          )}
        </article>

        <div className="analisi-detail-grid">

            <article className="analisi-chart-card">
              <div className="analisi-chart-heading">
                <div>
                  <span>CLIENTI</span>
                  <h3>Top clienti</h3>
                </div>

                <strong>
                  Top {topClienti.length}
                </strong>
              </div>

              {topClienti.length > 0 ? (
                <div className="analisi-chart analisi-chart-tall">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={topClienti}
                      layout="vertical"
                      margin={{
                        top: 8,
                        right: 20,
                        left: 8,
                        bottom: 4,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={false}
                        stroke="#dce5ee"
                      />

                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tick={{
                          fill: "#62778a",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        type="category"
                        dataKey="cliente"
                        width={190}
                        interval={0}
                        tick={<AnalisiClienteTick />}
                        tickLine={false}
                        axisLine={false}
                      />

                      <Tooltip
                        labelFormatter={(label) =>
                          label
                        }
                        formatter={(value) => [
                          formatNumber(value),
                          "Interventi",
                        ]}
                        contentStyle={{
                          borderRadius: 12,
                          border:
                            "1px solid #d9e3ec",
                        }}
                      />

                      <Bar
                        dataKey="interventi"
                        fill="#315f8f"
                        radius={[0, 7, 7, 0]}
                        maxBarSize={25}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="analisi-empty">
                  Nessun cliente nel periodo.
                </div>
              )}
            </article>


            <article className="analisi-chart-card">
              <div className="analisi-chart-heading">
                <div>
                  <span>TECNICI</span>
                  <h3>Ore per tecnico</h3>
                </div>

                <strong>
                  {tecnici.length}
                </strong>
              </div>

              {tecnici.length > 0 ? (
                <div className="analisi-chart analisi-chart-tall">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <BarChart
                      data={tecnici}
                      layout="vertical"
                      margin={{
                        top: 8,
                        right: 20,
                        left: 4,
                        bottom: 4,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={false}
                        stroke="#dce5ee"
                      />

                      <XAxis
                        type="number"
                        tick={{
                          fill: "#62778a",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <YAxis
                        type="category"
                        dataKey="tecnico"
                        width={90}
                        tick={{
                          fill: "#4d667b",
                          fontSize: 11,
                        }}
                        tickLine={false}
                        axisLine={false}
                      />

                      <Tooltip
                        formatter={(value) => [
                          `${formatNumber(
                            value,
                            2
                          )} h`,
                          "Ore conteggiate",
                        ]}
                        contentStyle={{
                          borderRadius: 12,
                          border:
                            "1px solid #d9e3ec",
                        }}
                      />

                      <Bar
                        dataKey="ore_conteggiate"
                        fill="#3d78ad"
                        radius={[0, 7, 7, 0]}
                        maxBarSize={30}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="analisi-empty">
                  Nessuna ora tecnico registrata.
                </div>
              )}
            </article>


            <article className="analisi-chart-card analisi-status-card">
              <div className="analisi-chart-heading">
                <div>
                  <span>PREVENTIVI</span>
                  <h3>Distribuzione per stato</h3>
                </div>

                <strong>
                  {formatNumber(
                    preventivi.totale
                  )}
                </strong>
              </div>

              {preventiviStati.length > 0 ? (
                <>
                  <div className="analisi-chart analisi-chart-medium">
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                    >
                      <BarChart
                        data={preventiviStati}
                        margin={{
                          top: 8,
                          right: 20,
                          left: -10,
                          bottom: 4,
                        }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="#dce5ee"
                        />

                        <XAxis
                          dataKey="stato"
                          tick={{
                            fill: "#4d667b",
                            fontSize: 11,
                          }}
                          tickLine={false}
                          axisLine={false}
                        />

                        <YAxis
                          allowDecimals={false}
                          tick={{
                            fill: "#62778a",
                            fontSize: 11,
                          }}
                          tickLine={false}
                          axisLine={false}
                        />

                        <Tooltip
                          formatter={(
                            value,
                            name
                          ) => [
                            formatNumber(value),
                            name === "totale"
                              ? "Preventivi"
                              : name,
                          ]}
                          contentStyle={{
                            borderRadius: 12,
                            border:
                              "1px solid #d9e3ec",
                          }}
                        />

                        <Bar
                          dataKey="totale"
                          fill="#315f8f"
                          radius={[7, 7, 2, 2]}
                          maxBarSize={70}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="analisi-status-list">
                    {preventiviStati.map(
                      (item) => (
                        <div
                          className="analisi-status-row"
                          key={item.stato}
                        >
                          <div>
                            <strong>
                              {item.stato}
                            </strong>

                            <span>
                              {formatNumber(
                                item.totale
                              )}{" "}
                              preventivi
                            </span>
                          </div>

                          <div className="analisi-status-values">
                            <strong>
                              {formatNumber(
                                item.percentuale,
                                1
                              )}
                              %
                            </strong>

                            <span>
                              {formatCurrency(
                                item.valore
                              )}
                            </span>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </>
              ) : (
                <div className="analisi-empty">
                  Nessun preventivo nel periodo.
                </div>
              )}
            </article>

          </div>

          <div className="analisi-info-grid">
            <article className="analisi-info-card">
              <span className="analisi-info-label">
                Ore tecnici
              </span>

              <strong>
                {formatNumber(
                  giornate.ore_effettive,
                  2
                )}{" "}
                h effettive
              </strong>

              <p>
                {formatNumber(
                  giornate.ore_conteggiate,
                  2
                )}{" "}
                h conteggiate su{" "}
                {formatNumber(
                  giornate.interventi
                )}{" "}
                interventi registrati.
              </p>
            </article>

            <article className="analisi-info-card">
              <span className="analisi-info-label">
                Flotta
              </span>

              <strong>
                {formatNumber(
                  flotta.km,
                  2
                )}{" "}
                km
              </strong>

              <p>
                {formatNumber(
                  flotta.ore_viaggio,
                  2
                )}{" "}
                ore di viaggio registrate.
              </p>
            </article>

            <article className="analisi-info-card">
              <span className="analisi-info-label">
                Materiali
              </span>

              <strong>
                {formatNumber(
                  materiali.quantita,
                  2
                )}{" "}
                unità
              </strong>

              <p>
                Valore associato agli interventi:{" "}
                {formatCurrency(
                  materiali.valore
                )}.
              </p>
            </article>
          </div>

          <div className="analisi-data-note">
            <strong>Nota sui dati</strong>
            <span>
              Le ore tecnici e i dati Flotta
              rappresentano solo lo storico
              effettivamente registrato nei relativi
              moduli.
            </span>
          </div>
        </>
      )}
    </section>
  );
};

export default AnalisiDashboard;
